import { useEffect, useState, type CSSProperties } from 'react'
import { useRouter } from 'next/router'
import Link from 'next/link'
import clsx from 'clsx'

import { Popover, PopoverContent, PopoverTrigger } from '@wartek-id/popover'
import { Button } from '@wartek-id/button-v1'
import { Input, InputGroup, InputLeftAddon } from '@wartek-id/input'
import { Icon } from '@wartek-id/icon-v2'

import { assetUrl } from 'utils/url'
import { useAuth } from 'utils/auth'

function ProfileButton() {
  const { session, login, logout } = useAuth()

  const handleLogin = async () => {
    try {
      login()
    } catch (error) {
      console.error('Error during login', error)
    }
  }

  const handleLogout = async () => {
    try {
      await logout()
    } catch (error) {
      console.error('Error during logout', error)
    }
  }

  // Extract user info from session
  const user = session?.user
  const isLoggedIn = !!session
  const userInitial = user?.name?.split(' ')[0]?.slice(0, 2) || 'U'

  return (
    <>
      {isLoggedIn ? (
        <Popover onClose={() => {}} onOpen={() => {}} placement="bottom-end">
          <PopoverTrigger
            as="button"
            className="cursor-pointer"
            aria-label="Menu profil pengguna"
          >
            <div className="flex items-center text-action md:gap-2">
              <div className="flex items-center gap-2 text-base">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-30 md:h-12 md:w-12">
                  <span
                    className="text-heading-sm uppercase md:text-heading-md"
                    aria-hidden="true"
                  >
                    {userInitial}
                  </span>
                </div>
              </div>

              <Icon aria-hidden="true">expand_more</Icon>
            </div>
          </PopoverTrigger>
          <PopoverContent
            className="mt-2 min-w-60"
            role="menu"
            aria-label="Menu Profil"
          >
            <div className="flex flex-col gap-2 px-4 py-5">
              <div role="none">
                <p className="font-semibold">{user?.name || 'User'}</p>
                <p className="text-sm">{user?.email || ''}</p>
              </div>
              <div className="mb-6 mt-3 border-t border-subdued" role="none" />
              <Button
                color="white"
                className="space-x-2 !text-critical"
                onClick={handleLogout}
                role="menuitem"
                aria-label="Keluar dari akun"
                style={{
                  // @ts-expect-error This is a valid CSS variable
                  '--borderColor': 'var(--color-border-critical-default)',
                }}
              >
                <Icon color="inherit" aria-hidden="true">
                  exit_to_app
                </Icon>
                <span>Logout</span>
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      ) : (
        <>
          <div className="md:hidden">
            <Button
              color="black"
              size="md"
              onClick={handleLogin}
              aria-label="Masuk ke akun"
            >
              Masuk
            </Button>
          </div>
          <div className="hidden md:block">
            <Button
              color="black"
              size="lg"
              onClick={handleLogin}
              aria-label="Masuk ke akun"
            >
              Masuk
            </Button>
          </div>
        </>
      )}
    </>
  )
}

export function Navbar() {
  const router = useRouter()
  const [search, setSearch] = useState<string>('')

  useEffect(() => {
    setSearch((router.query.q as string) || '')
  }, [router.query.q])

  return (
    <nav
      data-testid="navbar"
      className="flex mx-auto w-full md:max-w-4xl lg:max-w-5xl xl:max-w-7xl 2xl:max-w-screen-2xl flex-col items-start px-4 py-3 pt-6 sm:py-4 md:flex-row md:items-center md:justify-between md:gap-8 md:px-6 lg:px-8"
      aria-label="Primary Navigation"
    >
      <div className="flex w-full flex-shrink items-center justify-between gap-4 md:w-auto">
        <Link
          passHref
          href={process.env.NEXT_PUBLIC_RUMAH_PENDIDIKAN_URL}
          className="h-8 w-auto md:h-10 lg:pb-4"
          aria-label="Rumah Pendidikan Home"
        >
          <a>
            <img
              src={assetUrl('/images/ruang-gtk-v2/images/rumah-pendidikan.png')}
              alt="Rumah Pendidikan"
              width={136}
              height={40}
              className="h-8 w-auto md:h-10"
            />
          </a>
        </Link>

        <div className="flex h-12 items-center md:hidden">
          <ProfileButton />
        </div>
      </div>

      <div className="w-full flex-1 pt-3 md:pt-0">
        <div className="md:max-w-xl lg:mx-auto lg:max-w-2xl">
          <form
            className="relative flex items-center md:max-w-lg lg:mx-auto lg:max-w-2xl lg:pb-0"
            role="search"
            aria-label="Pencarian layanan pendidikan"
            onSubmit={(e) => {
              e.preventDefault()
              if (!search) return

              router.push({
                pathname: '/pencarian',
                query: { q: search },
              })
            }}
          >
            <InputGroup>
              <InputLeftAddon className="pl-4" style={{ maxHeight: 'inherit' }}>
                <Icon aria-hidden className="text-icon-subdued">
                  search
                </Icon>
              </InputLeftAddon>
              <Input
                placeholder="Contoh: Pembelajaran, Panduan, dsb..."
                style={
                  {
                    '--padding-right-input-area': '100px',
                    '--padding-left-input-area': '52px',
                  } as CSSProperties
                }
                className="rounded-lg border-subdued focus:!border-primary md:h-16"
                autoComplete="off"
                aria-label="Cari layanan pendidikan"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </InputGroup>
            <Button
              className={clsx(
                'absolute right-0 top-0 h-full md:right-2 md:top-2 md:h-auto',
                'hover:bg-surface-hover'
              )}
              size="lg"
              aria-label="Mulai pencarian"
              type="submit"
            >
              Cari
            </Button>
          </form>
        </div>
      </div>

      <div className="hidden h-12 items-center md:flex">
        <ProfileButton />
      </div>
    </nav>
  )
}
